import React from 'react';
import { ContextualMenuItemType, IconButton, IContextualMenuItem } from 'office-ui-fabric-react';
import { ExtensionInfo, ExtensionLocation, ExtensionPageLocation, ExtensionType, findExtension } from '../../store/ExtensionStore';
import { EntityType, entityTypeLabelMap, IBaseEntity } from '../../entities/common';
import ExtensionPanel from './ExtensionPanel';
import { buildExtensionsMenuItem } from './headerMenuItemBuilders';
import SharePanel from './SharePanel';
import { ProjectInfo } from '../../store/ProjectsListStore';
import { Portfolio } from '../../store/PortfoliosListStore';
import { Program } from '../../store/ProgramsListStore';
import { Roadmap } from '../../store/roadmap/common';
import { Challenge } from '../../store/ChallengesListStore';
import { Resource } from '../../store/ResourcesListStore';
import { LayoutsState } from '../../store/layouts';
import { notUndefined } from '../utils/common';
import { ApplicationState } from '../../store';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { TenantState, PPMFeatures, Subscription } from '../../store/Tenant';
import { UserState } from '../../store/User';
import * as analytics from '../../analytics';
import { buildPresentationModeItemIfEnabled } from '../project/ProjectsList';
import * as AiAgentsStore from '../../store/AiAgentsStore';
import { buildActivityLogMenuItem } from '../audit/MenuWithAuditLog';

type OwnProps = {
    entity: IBaseEntity & (ProjectInfo | Portfolio | Program | Roadmap | Challenge | Resource);
    entityType: EntityType;
    items: IContextualMenuItem[];
    disabled?: boolean;
    onClickMenuItem?: (name: string) => void;
}

type StateProps = {
    extensions: ExtensionInfo[];
    layouts: LayoutsState;
    user: UserState;
    tenant: TenantState;
    aiAgents: AiAgentsStore.AiAgentsState;
}

type Props = OwnProps & StateProps & RouteComponentProps<{}>;

const EntityHeaderMoreMenu = (props: Props) => {
    const { entity, entityType, extensions, layouts, disabled, user, aiAgents, tenant } = props;
    const [isSharePanelOpen, setIsSharePanelOpen] = React.useState(false);
    const [isExtensionPanelOpen, setIsExtensionPanelOpen] = React.useState<ExtensionInfo>();
    const [isAiAgentsPanelOpen, setIsAiAgentsPanelOpen] = React.useState<boolean>(false);

    const menuItems = [
        entityType !== EntityType.Idea && entityType !== EntityType.Resource &&  entityType !== EntityType.ArchivedProject ? {
            key: 'share',
            iconProps: { iconName: 'Share' },
            name: 'Share',
            disabled: !entity.isEditable,
            onClick: () => {
                setIsSharePanelOpen(true);
                analytics.track(`Share ${entityTypeLabelMap[entityType].singular}`);
                props.onClickMenuItem?.('Share');
            }
        } : undefined,

        entityType === EntityType.Project
            ? buildPresentationModeItemIfEnabled(props.tenant, user, entity as ProjectInfo,
                () => {
                    props.history.replace({ ...props.location, state: { ...props.location.state, presentationMode: true } });
                    props.onClickMenuItem?.('Presentation Mode');
                })
            : undefined,
        
        // Regular extensions (excluding AI Agents)
        ...(entity.isEditable ? buildExtensionsMenuItem(extensions, setIsExtensionPanelOpen) : []),

        // Add AI Agents menu item if feature is enabled and there are AI Agent extensions
        ...(entity.isEditable && entityType === EntityType.Project && Subscription.contains(tenant.subscription, PPMFeatures.AiAgents) && aiAgents.tenantStatus.hasTenant ? [{
            key: 'aiagents',
            iconProps: { iconName: 'Robot' },
            className: "generate-with-ai",
            name: 'AI Agents',
            onClick: () => {
                setIsAiAgentsPanelOpen(true);
                analytics.track(`Open AI Agents for ${entityTypeLabelMap[entityType].singular}`);
                props.onClickMenuItem?.('AI Agents');
            }
        }] : []),
        ...props.items,
        { key: 'audit-divider', itemType: ContextualMenuItemType.Divider },
        buildActivityLogMenuItem(props.tenant.subscription, props.history, entityType, entity.id, props.onClickMenuItem)
    ].filter(notUndefined);

    return <>
        <IconButton
            menuIconProps={{ iconName: 'More' }}
            className='menu'
            text='More'
            disabled={disabled}
            menuProps={{ items: menuItems, className: 'section-menu' }} />
        {isSharePanelOpen && <SharePanel
            key="share-panel"
            entity={entity as ProjectInfo | Portfolio | Program | Roadmap | Challenge}
            entityType={entityType}
            layouts={layouts}
            hideView={entityType === EntityType.Challenge}
            hideCollaborate={entityType === EntityType.Roadmap}
            onDismiss={() => setIsSharePanelOpen(false)} />
        }
        {isExtensionPanelOpen && <ExtensionPanel
            entityType={entityType}
            info={isExtensionPanelOpen}
            onDismiss={() => setIsExtensionPanelOpen(undefined)}
            context={{ entityId: entity.id, entityType }}
        />}
        {isAiAgentsPanelOpen && <ExtensionPanel
            entityType={entityType}
            info={{
                name: 'AI Agents',
                url: aiAgents.clientUrl,
                type: ExtensionType.EmbeddedContent,
                location: ExtensionLocation.ProjectDashboardActionItems,
                id: 'aiagents'
            }}
            onDismiss={() => setIsAiAgentsPanelOpen(false)}
            context={{ entityId: entity.id, entityType }}
        />}
    </>
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        layouts: state.layouts[ownProps.entityType],
        extensions: findExtension(state.extensions, ownProps.entityType, ExtensionPageLocation.EntityAction),
        user: state.user,
        tenant: state.tenant,
        aiAgents: state.aiAgents
    }
}

export default withRouter<OwnProps>(connect(mapStateToProps)(EntityHeaderMoreMenu));